<template>
  <section class="listing">
    <div class="header">
      <div class="title">Comissão</div>
      <div class="header-actions">
        <button
          v-if="selectedPartner"
          @click="openEditPartner"
          class="button-action"
          type="button"
        >
          <i class="fas fa-pen"></i><span>EDITAR</span>
        </button>
        <button @click="openNewPartner" class="button-new" type="button">
          <i class="fas fa-plus"></i><span>PARCEIRO</span>
        </button>
      </div>
    </div>

    <form @submit.prevent class="form form-search">
      <input v-model="iniDate" @change="getReport" type="date" />
      <input v-model="endDate" @change="getReport" type="date" />
      <select v-model="partnerId" @change="onPartnerChange" class="partner-select">
        <option value="">Selecione o parceiro</option>
        <option v-for="p in partners" :key="p.id" :value="String(p.id)">
          {{ partnerLabel(p) }}
        </option>
      </select>
    </form>

    <transition name="show" mode="out-in">
      <i v-if="loading" key="loading" class="fas fa-spinner loading-big"></i>
      <div v-else-if="!partnerId" key="empty" class="empty-hint">
        Selecione o período e o parceiro para listar as vendas e a comissão.
      </div>
      <div v-else key="report" style="margin-top: 10px">
        <div class="sum-group">
          <div class="sum">
            <span>Comissão ({{ commissionLabel }})</span>
            <span>{{ commission.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}</span>
          </div>
          <div class="sum">
            <span>Total vendas</span>
            <span>{{ totalAmount.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}</span>
          </div>
          <div class="sum">
            <span>Registros</span>
            <span>{{ items.length }}</span>
          </div>
        </div>

        <div class="list-cards">
          <div v-for="(t, i) in items" :key="i" class="list list-row">
            <div class="data">
              <div class="main-data">{{ t.nome }}</div>
              <div class="detail-data">{{ t.produto }}</div>
              <div class="detail-data">{{ t.data }} · Fonte: {{ t.fonte }}</div>
            </div>
            <div class="list-meta">
              <div class="main-data">{{ Number(t.valor).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}</div>
            </div>
          </div>
          <div v-if="!items.length" class="empty-hint">
            Nenhuma venda encontrada para este parceiro no período.
          </div>
        </div>
      </div>
    </transition>

    <Partner
      v-if="showPartner"
      :partner="editingPartner"
      :close="onPartnerClose"
    />
  </section>
</template>

<script>
import logger from '../../logger'
import { getPartners, getReport } from '../api/commission'
import Partner from './Partner.vue'

export default {
  name: 'Commission',
  components: { Partner },
  data () {
    return {
      loading: false,
      partners: [],
      partnerId: '',
      iniDate: null,
      endDate: null,
      items: [],
      totalAmount: 0,
      commission: 0,
      commissionLabel: '',
      showPartner: false,
      editingPartner: null
    }
  },
  computed: {
    selectedPartner () {
      if (!this.partnerId) return null
      return this.partners.find(p => String(p.id) === String(this.partnerId)) || null
    }
  },
  async mounted () {
    try {
      const dateIni = new Date()
      const dateFim = new Date()
      dateFim.setMonth(dateFim.getMonth() + 1)
      dateFim.setDate(1)
      dateFim.setDate(dateFim.getDate() - 1)
      this.endDate = dateFim.toISOString().split('T')[0]
      dateIni.setDate(1)
      this.iniDate = dateIni.toISOString().split('T')[0]
      await this.loadPartners()
    } catch (error) {
      logger.error(error, this)
    }
  },
  methods: {
    partnerLabel (p) {
      const type = p.commissionType || p.commission_type || 'percent'
      const value = Number(p.percent || 0)
      if (type === 'fixed') {
        return `${p.name} (${value.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })} / venda)`
      }
      return `${p.name} (${value.toLocaleString('pt-br')}%)`
    },
    openNewPartner () {
      this.editingPartner = null
      this.showPartner = true
    },
    openEditPartner () {
      if (!this.selectedPartner) return
      this.editingPartner = { ...this.selectedPartner }
      this.showPartner = true
    },
    onPartnerChange () {
      this.getReport()
    },
    async loadPartners () {
      try {
        this.partners = (await getPartners()) || []
      } catch (error) {
        logger.error(error, this)
        this.partners = []
      }
    },
    async getReport () {
      if (!this.partnerId || !this.iniDate || !this.endDate) {
        this.items = []
        this.totalAmount = 0
        this.commission = 0
        this.commissionLabel = ''
        return
      }
      this.loading = true
      try {
        const report = await getReport(this.partnerId, this.iniDate, this.endDate)
        this.items = (report && report.items) || []
        this.totalAmount = Number((report && report.totalAmount) || 0)
        this.commission = Number((report && report.commission) || 0)
        const partner = report && report.partner
        const type = (partner && (partner.commissionType || partner.commission_type)) || 'percent'
        const value = Number((partner && (partner.value != null ? partner.value : partner.percent)) || 0)
        this.commissionLabel = type === 'fixed'
          ? `${value.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })} / venda`
          : `${value.toLocaleString('pt-br')}%`
      } catch (error) {
        logger.error(error, this)
        this.items = []
        this.totalAmount = 0
        this.commission = 0
        this.commissionLabel = ''
      }
      this.loading = false
    },
    async onPartnerClose (saved) {
      this.showPartner = false
      this.editingPartner = null
      if (saved) {
        const currentId = this.partnerId
        await this.loadPartners()
        if (currentId && !this.partners.some(p => String(p.id) === String(currentId))) {
          this.partnerId = ''
        }
        await this.getReport()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../_asset/scss/_variables";

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.partner-select {
  min-width: 200px;
  flex: 1;
}

.empty-hint {
  padding: 24px 15px;
  color: $lp-text-2;
  text-align: center;
}
</style>
